import { useEffect, useState } from "react";
import { Line, Bar, Pie, Doughnut } from "react-chartjs-2";

import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from "chart.js";
import "./Dashboard.css";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
);


export default function Dashboard() {
  const [logs, setLogs] = useState([]);
  const [category, setCategory] = useState("Work");
  const [dateRange, setDateRange] = useState({ start: "", end: "" });
  const [selectedMonth, setSelectedMonth] = useState("");
  const [selectedQuarter, setSelectedQuarter] = useState("");

  useEffect(() => {
    const token = localStorage.getItem("token") || sessionStorage.getItem("token");

    // ✅ Use relative URL in production (same domain), absolute in dev
    const API_URL = process.env.NODE_ENV === "production"
      ? ""
      : (process.env.REACT_APP_API_URL || "http://localhost:5000");

    fetch(`${API_URL}/api/progress`, {
      headers: { "Authorization": `Bearer ${token}` }
    })
      .then(res => res.json())
      .then(data => {
        // ✅ Guard against non-array response
        setLogs(Array.isArray(data) ? data : []);
      })
      .catch(err => {
        console.error("Error fetching logs:", err);
        setLogs([]); // ✅ safe fallback
      });
  }, []);

  // Filter by category
  const categoryLogs = logs.filter(l => l.category === category);

  // Apply date/month/quarter filters
  const filteredLogs = categoryLogs.filter(l => {
    if (dateRange.start && new Date(l.date) < new Date(dateRange.start)) return false;
    if (dateRange.end && new Date(l.date) > new Date(dateRange.end)) return false;
    if (selectedMonth && l.month && l.month !== selectedMonth) return false;
    if (selectedQuarter && l.quarter && l.quarter !== selectedQuarter) return false;
    return true;
  });

  // Summary calculations
  const totalHours = filteredLogs.reduce((sum, l) => sum + (l.hours || 0), 0);
  const avgCompletion = filteredLogs.filter(l => l.completion !== undefined).length
    ? (filteredLogs.reduce((sum, l) => sum + (l.completion || 0), 0) /
       filteredLogs.filter(l => l.completion !== undefined).length).toFixed(1)
    : 0;
  const taskCount = filteredLogs.length;
  const avgHours = taskCount > 0 ? (totalHours / taskCount).toFixed(1) : 0;

  // Charts configuration
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: true,
    plugins: {
      legend: {
        display: true,
        position: "top",
        labels: { usePointStyle: true, padding: 15 }
      }
    }
  };

  // Charts
  const lineData = {
    labels: filteredLogs.length > 0 ? filteredLogs.map(l => l.date || l.week || l.month || l.quarter) : ["No data"],
    datasets: [
      {
        label: `${category} Hours`,
        data: filteredLogs.length > 0 ? filteredLogs.map(l => l.hours || 0) : [0],
        borderColor: "rgb(102, 126, 234)",
        backgroundColor: "rgba(102, 126, 234, 0.1)",
        borderWidth: 2,
        tension: 0.4,
        fill: true,
        pointRadius: 5,
        pointHoverRadius: 7
      }
    ]
  };

  const barData = {
    labels: filteredLogs.length > 0 ? filteredLogs.map(l => l.date || l.week || l.month || l.quarter) : ["No data"],
    datasets: [
      {
        label: "Completion %",
        data: filteredLogs.length > 0 ? filteredLogs.map(l => l.completion || 0) : [0],
        backgroundColor: "rgba(118, 183, 178, 0.7)",
        borderColor: "rgb(118, 183, 178)",
        borderWidth: 1,
        borderRadius: 8
      }
    ]
  };

  const categoryDistribution = {
    labels: ["Work", "Learning", "SelfDevelopment", "Fitness"],
    datasets: [
      {
        label: "Hours Distribution",
        data: [
          logs.filter(l => l.category === "Work").reduce((sum, l) => sum + (l.hours || 0), 0),
          logs.filter(l => l.category === "Learning").reduce((sum, l) => sum + (l.hours || 0), 0),
          logs.filter(l => l.category === "SelfDevelopment").reduce((sum, l) => sum + (l.hours || 0), 0),
          logs.filter(l => l.category === "Fitness").reduce((sum, l) => sum + (l.hours || 0), 0)
        ],
        backgroundColor: [
          "rgba(102, 126, 234, 0.8)",
          "rgba(118, 183, 178, 0.8)",
          "rgba(255, 193, 7, 0.8)",
          "rgba(244, 67, 54, 0.8)"
        ],
        borderColor: [
          "rgb(102, 126, 234)",
          "rgb(118, 183, 178)",
          "rgb(255, 193, 7)",
          "rgb(244, 67, 54)"
        ],
        borderWidth: 2
      }
    ]
  };

  const pieData = {
    labels: filteredLogs.filter(l => l.goals).map(l => l.goals),
    datasets: [
      {
        label: "Completion %",
        data: filteredLogs.filter(l => l.completion !== undefined).map(l => l.completion),
        backgroundColor: ["#36A2EB", "#FF6384", "#FFCE56", "#4BC0C0", "#9966FF"],
        borderColor: ["#fff", "#fff", "#fff", "#fff", "#fff"],
        borderWidth: 2
      }
    ]
  };

  return (
    <div className="dashboard-container">
      <div className="dashboard-header">
        <h1>📊 Dashboard</h1>
        <p>Track your progress across categories</p>
      </div>

      {/* Summary Cards */}
      <div className="summary-cards">
        <div className="summary-card">
          <div className="card-icon">⏱️</div>
          <div className="card-value">{totalHours}h</div>
          <div className="card-label">Total Hours</div>
        </div>
        <div className="summary-card">
          <div className="card-icon">📈</div>
          <div className="card-value">{avgHours}h</div>
          <div className="card-label">Avg per Task</div>
        </div>
        <div className="summary-card">
          <div className="card-icon">✅</div>
          <div className="card-value">{taskCount}</div>
          <div className="card-label">Tasks Done</div>
        </div>
        <div className="summary-card">
          <div className="card-icon">🎯</div>
          <div className="card-value">{avgCompletion}%</div>
          <div className="card-label">Avg Completion</div>
        </div>
      </div>

      {/* Filters Section */}
      <div className="filters-section">
        <div className="filter-group">
          <label>Category:</label>
          <select value={category} onChange={(e) => setCategory(e.target.value)} className="filter-select">
            <option value="Work">Work</option>
            <option value="Learning">Learning</option>
            <option value="SelfDevelopment">Self Development</option>
            <option value="Fitness">Fitness</option>
          </select>
        </div>

        <div className="filter-group">
          <label>Start Date:</label>
          <input
            type="date"
            value={dateRange.start}
            onChange={(e) => setDateRange({ ...dateRange, start: e.target.value })}
            className="filter-input"
          />
        </div>

        <div className="filter-group">
          <label>End Date:</label>
          <input
            type="date"
            value={dateRange.end}
            onChange={(e) => setDateRange({ ...dateRange, end: e.target.value })}
            className="filter-input"
          />
        </div>

        <div className="filter-group">
          <label>Month:</label>
          <select value={selectedMonth} onChange={(e) => setSelectedMonth(e.target.value)} className="filter-select">
            <option value="">All Months</option>
            <option value="January">January</option>
            <option value="February">February</option>
            <option value="March">March</option>
            <option value="April">April</option>
            <option value="May">May</option>
            <option value="June">June</option>
          </select>
        </div>

        <div className="filter-group">
          <label>Quarter:</label>
          <select value={selectedQuarter} onChange={(e) => setSelectedQuarter(e.target.value)} className="filter-select">
            <option value="">All Quarters</option>
            <option value="Q1">Q1</option>
            <option value="Q2">Q2</option>
            <option value="Q3">Q3</option>
            <option value="Q4">Q4</option>
          </select>
        </div>
      </div>

      {/* Charts Grid */}
      <div className="charts-grid">
        <div className="chart-container large">
          <h2>{category} Hours Trend</h2>
          <Line data={lineData} options={chartOptions} />
        </div>

        <div className="chart-container">
          <h2>Progress Rate</h2>
          <Bar data={barData} options={chartOptions} />
        </div>

        <div className="chart-container">
          <h2>Category Distribution</h2>
          <Doughnut data={categoryDistribution} options={chartOptions} />
        </div>

        {pieData.datasets[0].data.length > 0 && (
          <div className="chart-container">
            <h2>Goals Breakdown</h2>
            <Pie data={pieData} options={chartOptions} />
          </div>
        )}
      </div>

      {/* All Logs */}
      {filteredLogs.length > 0 && (
        <div className="logs-section">
          <h2>Recent Logs ({filteredLogs.length})</h2>
          <table className="logs-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Topic</th>
                <th>Hours</th>
                <th>Completion</th>
                <th>Achievement</th>
              </tr>
            </thead>
            <tbody>
              {filteredLogs.map((l, i) => (
                <tr key={i} className="log-row">
                  <td>{l.date || l.week || l.month || l.quarter}</td>
                  <td>{l.topic || l.goals}</td>
                  <td><strong>{l.hours || 0}h</strong></td>
                  <td>
                    <span className="completion-badge" style={{
                      backgroundColor: (l.completion || 0) >= 75 ? "#4caf50" : (l.completion || 0) >= 50 ? "#ff9800" : "#f44336"
                    }}>
                      {l.completion || 0}%
                    </span>
                  </td>
                  <td>{l.achievement || l.achievements || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}